// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Alert, Badge, Button, ChevronRightIcon } from '@loa/ui-components';
import {
  suggestionsApi,
  type RelationshipSuggestion,
} from '../../api/suggestionsApi';

/** Badge colour by step type, matching the design (cashflow = blue, LoA = orange). */
function badgeColor(type: string): 'blue' | 'orange' | 'purple' | 'grey' {
  switch (type) {
    case 'CASHFLOW_MODEL':
      return 'blue';
    case 'CREATE_LOA':
      return 'orange';
    case 'REVIEW':
      return 'purple';
    default:
      return 'grey';
  }
}

/** Primary-action label by action kind. */
function executeLabel(actionKind: string): string {
  switch (actionKind) {
    case 'EXECUTE_CASHFLOW_MODEL':
      return 'Create cashflow model';
    case 'OPEN_LOA_FLOW':
      return 'Create LoA';
    case 'LINK':
      return 'Open';
    default:
      return 'Take action';
  }
}

/**
 * Panel 2 of the relationship Overview — the actionable "Suggested Steps" cards.
 * Each card carries a type badge, a title, a rationale, and Execute / Dismiss
 * actions. Executing a CASHFLOW_MODEL step creates the what-if scenario and
 * routes the advisor into the cashflow view. Renders nothing when there are no
 * active suggestions. See docs/specs/relationship-summary-panels-prd.md §7.
 */
export function SuggestedStepsPanel({ profileId }: { profileId: string }) {
  const [items, setItems] = useState<RelationshipSuggestion[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchOnce = useCallback(async () => {
    try {
      setItems(await suggestionsApi.list(profileId));
      setError(null);
    } catch {
      setError('Could not load suggested steps.');
    } finally {
      setLoaded(true);
    }
  }, [profileId]);

  useEffect(() => {
    void fetchOnce();
  }, [fetchOnce]);

  const onExecute = useCallback(
    async (s: RelationshipSuggestion) => {
      setActingId(s.id);
      setNotice(null);
      try {
        const result = await suggestionsApi.execute(profileId, s.id);
        setItems((prev) => prev.filter((x) => x.id !== s.id));
        if (result.partial && result.notes?.length) {
          setNotice(`Created — some steps need manual setup: ${result.notes.join('; ')}`);
        }
        if (result.deepLink) {
          navigate(result.deepLink);
        }
      } catch {
        setError('Could not execute that step.');
      } finally {
        setActingId(null);
      }
    },
    [profileId, navigate],
  );

  const onDismiss = useCallback(
    async (s: RelationshipSuggestion) => {
      setActingId(s.id);
      try {
        await suggestionsApi.dismiss(profileId, s.id);
        setItems((prev) => prev.filter((x) => x.id !== s.id));
      } catch {
        setError('Could not dismiss that step.');
      } finally {
        setActingId(null);
      }
    },
    [profileId],
  );

  // Nothing to show until loaded, and no card when there's no advice + no error.
  if (!loaded) {
    return null;
  }
  if (items.length === 0 && !error && !notice) {
    return null;
  }

  return (
    <div
      data-testid="suggested-steps-panel"
      className="bg-white rounded-card p-lg mb-md"
      style={{ border: '1px solid #F0F0F0' }}
    >
      <h3 className="text-[18px] font-semibold text-black m-0 mb-md">Suggested Steps</h3>
      {error && <Alert variant="error">{error}</Alert>}
      {notice && (
        <div className="mb-[12px]" data-testid="suggested-steps-notice">
          <Alert variant="info">{notice}</Alert>
        </div>
      )}
      <div className="flex flex-col gap-[12px]">
        {items.map((s) => (
          <div
            key={s.id}
            data-testid="suggestion-card"
            className="rounded-button p-md flex items-start gap-[12px]"
            style={{ border: '1.5px solid #BDB8FF' }}
          >
            <div className="flex-1 min-w-0">
              {s.badgeLabel && (
                <Badge color={badgeColor(s.type)} size="sm" data-testid="suggestion-badge">
                  {s.badgeLabel}
                </Badge>
              )}
              <p className="text-body-02 font-semibold text-black mt-sm mb-xs">{s.title}</p>
              {s.body && (
                <div className="text-body-03 text-[#595959] suggestion-body">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml>
                    {s.body}
                  </ReactMarkdown>
                </div>
              )}
              {s.reviewNote && (
                <p className="text-body-04 italic text-[#8C8C8C] mt-xs mb-0">Review: {s.reviewNote}</p>
              )}
              <div className="flex items-center gap-sm mt-[12px]">
                {s.executable && (
                  <Button
                    size="sm"
                    onClick={() => onExecute(s)}
                    disabled={actingId === s.id}
                    data-testid="suggestion-execute"
                  >
                    {actingId === s.id ? 'Working…' : executeLabel(s.actionKind)}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outlined"
                  onClick={() => onDismiss(s)}
                  disabled={actingId === s.id}
                  data-testid="suggestion-dismiss"
                >
                  Dismiss
                </Button>
              </div>
            </div>
            <ChevronRightIcon aria-hidden />
          </div>
        ))}
      </div>
    </div>
  );
}

export default SuggestedStepsPanel;
